import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Route as RouteIcon,
  Car, Cone, RefreshCw, Radar,
} from "lucide-react";
import { Button } from "../components/ui/button";
import api from "../lib/api";
import HeroBackdrop from "../components/HeroBackdrop";
import { useContent } from "../context/ContentContext";

const APP_BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolvePhoto = (u) => (u && u.startsWith("/") ? `${APP_BACKEND}${u}` : u);
const teamInitials = (n) => (n || "?").split(" ").map((x) => x[0]).join("").slice(0, 2).toUpperCase();

const IMG = {
  hero: "https://images.pexels.com/photos/13861/IMG_3496bfree.jpg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const TIER_LABEL = { head: "Head of Training", senior: "Advanced Tutor", assessor: "Advanced Tutor", tutor: "Advanced Tutor" };

const EXPERIENCE = [
  { icon: RouteIcon, title: "Observed drives", text: "Structured on-road drives with a tutor, then a debrief on your observation, positioning, progress and hazard management." },
  { icon: Car, title: "One-to-one coaching", text: "Personal, patient coaching matched to your car and your goals — from nervous motorway drivers to confident all-rounders." },
  { icon: Cone, title: "Manoeuvring & control", text: "Precision low-speed control and manoeuvring practice to build all-round confidence behind the wheel." },
  { icon: Radar, title: "Hazard & commentary drives", text: "Develop reading-the-road skills and a running commentary technique that keeps you a step ahead." },
  { icon: RefreshCw, title: "Continuous development", text: "Our tutors attend ongoing driving and training assessments, so the coaching you receive is always current." },
];

const ApplyButtons = ({ testid }) => (
  <div className="flex flex-col sm:flex-row gap-3" data-testid={testid}>
    <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-600 text-[#0F2744] font-bold px-8">
      <Link to="/join" data-testid={`${testid}-apply`}>Start your application <ArrowRight className="h-4 w-4 ml-2" /></Link>
    </Button>
    <Button asChild size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 hover:text-white px-8">
      <Link to="/contact" data-testid={`${testid}-contact`}>Ask the training team</Link>
    </Button>
  </div>
);

export default function DriverTraining() {
  const { t } = useContent();
  const [team, setTeam] = useState([]);
  useEffect(() => {
    api.get("/team", { params: { section: "training_driver" } }).then(({ data }) => setTeam(data)).catch(() => {});
  }, []);
  return (
    <div data-testid="driver-training-page">
      <section className="relative bg-[#0F2744] overflow-hidden">
        <HeroBackdrop image={t("img.drivertraining.hero", IMG.hero)} opacity={0.4} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold bg-amber-500 text-[#0F2744]">
            <Car className="h-4 w-4" /> Drivers · Training Team
          </div>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight max-w-3xl">
            Coached by the best, so you drive your best
          </h1>
          <p className="mt-5 text-lg text-slate-200 max-w-2xl leading-relaxed">
            Our driver training team is led by an experienced Head of Training, and every member holds the
            <strong> RoSPA Advanced Tutor</strong> qualification. They attend continuous driving and training
            assessments themselves — so the coaching you receive is always current, consistent and of the highest quality.
          </p>
          <div className="mt-8"><ApplyButtons testid="driver-training-hero-apply" /></div>
        </div>
      </section>

      <section className="bg-[#0F2744] py-20" data-testid="driver-training-structure">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white">A team of dedicated volunteers</h2>
          <p className="mt-6 text-lg text-slate-200 leading-relaxed">
            Our dedicated volunteer tutors — all approved RoSPA tutors, and holding the RoSPA Gold driving
            qualification — delivering one-to-one, on-road coaching.
          </p>
        </div>
      </section>

      <section className="bg-slate-50 py-20" data-testid="driver-training-people">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0F2744] text-center">Meet the driver training team</h2>
          {team.length === 0 ? (
            <p className="mt-8 text-center text-slate-500" data-testid="driver-team-empty">Our driver training team will appear here soon. In the meantime, <Link to="/contact" className="text-amber-600 font-semibold underline">get in touch</Link>.</p>
          ) : (
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {team.map((p) => (
                <div key={p.id} className="rounded-2xl bg-white border border-slate-200 p-6 flex gap-4" data-testid={`driver-team-${p.id}`}>
                  {p.photo ? <img src={resolvePhoto(p.photo)} alt={p.name} className="h-20 w-20 rounded-xl object-cover shrink-0" />
                    : <div className="h-20 w-20 rounded-xl bg-[#0F2744] text-white font-bold flex items-center justify-center shrink-0">{teamInitials(p.name)}</div>}
                  <div className="min-w-0">
                    <div className="font-bold text-[#0F2744]">{p.name}</div>
                    <div className="text-sm font-semibold text-amber-600">{p.role}{p.tier ? ` · ${TIER_LABEL[p.tier] || p.tier}` : ""}</div>
                    {p.grade && <div className="text-xs text-slate-500 mt-0.5">{p.grade}</div>}
                    {p.bio && <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.bio}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="driver-training-experience">
        <h2 className="text-3xl sm:text-4xl font-bold text-[#0F2744] text-center">What your training looks like</h2>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {EXPERIENCE.map((e) => (
            <div key={e.title} className="rounded-2xl bg-white border border-slate-200 p-6">
              <e.icon className="h-8 w-8 text-amber-500" />
              <h3 className="mt-4 font-bold text-[#0F2744] text-lg">{e.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{e.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#0F2744] py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white">Ready to drive with our Training Team?</h2>
          <p className="mt-3 text-slate-300">Start your application today and we'll be in touch about your first session.</p>
          <div className="mt-8 flex justify-center"><ApplyButtons testid="driver-training-cta" /></div>
        </div>
      </section>
    </div>
  );
}
